import { Minus, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import React, { useState, useEffect } from "react";
import { useAppSelector } from "@/store/hooks";
import { ScrollArea } from "../ui/scroll-area";
import { Cart } from "@/types/AppTypes";

interface cartComponentProps {
  cartItems?: Cart[] | any[];
  onUpdateQuantity?: (itemId: string, newQuantity: number) => void;
  onRemoveItem?: (itemId: string) => Promise<void>;
  isUpdating?: boolean;
  isRemoving?: boolean;
  isAuthenticated?: boolean;
  onPendingChangesChange?: (hasChanges: boolean) => void;
}

const CartProductTable = React.forwardRef<{ applyPendingUpdates: () => void }, cartComponentProps>(({ 
  cartItems, 
  onUpdateQuantity, 
  onRemoveItem, 
  isUpdating = false, 
  isRemoving = false,
  isAuthenticated = false,
  onPendingChangesChange
}, ref) => {
  const { brands } = useAppSelector((state) => state.app);
  const [pendingUpdates, setPendingUpdates] = useState<Record<string, number>>({});

  // Check if there are any pending changes and notify parent
  useEffect(() => {
    const hasChanges = Object.keys(pendingUpdates).length > 0;
    onPendingChangesChange?.(hasChanges);
  }, [pendingUpdates, onPendingChangesChange]);

  const handleQuantityChange = (item: any, newQuantity: number) => {
    if (isAuthenticated) {
      // For authenticated users, hold the change until "Update Cart" is clicked
      setPendingUpdates(prev => ({
        ...prev,
        [item.id]: newQuantity
      }));
    } else {
      // For guest users, update immediately
      if (onUpdateQuantity) {
        onUpdateQuantity(item.id, newQuantity);
      }
    }
  };

  const handleRemoveFromCart = async (item: any) => {
    if (onRemoveItem) {
      await onRemoveItem(item.id);
    }
    // Remove from pending updates if it exists
    setPendingUpdates(prev => {
      const newPending = { ...prev };
      delete newPending[item.id];
      return newPending;
    });
  };

  const getItemQuantity = (item: any) => {
    // Return pending quantity if it exists, otherwise return current quantity
    return pendingUpdates[item.id] !== undefined ? pendingUpdates[item.id] : (item?.quantity ?? 1);
  };

  // Function to apply pending updates (called from parent)
  const applyPendingUpdates = () => {
    Object.entries(pendingUpdates).forEach(([itemId, quantity]) => {
      if (quantity <= 0) {
        // Remove item if quantity is 0 or less
        onRemoveItem?.(itemId);
      } else {
        // Update quantity
        onUpdateQuantity?.(itemId, quantity);
      }
    });
    // Clear pending updates
    setPendingUpdates({});
  };

  // Expose applyPendingUpdates to parent
  React.useImperativeHandle(ref, () => ({
    applyPendingUpdates
  }));



  return (
    <>
      <ScrollArea className="w-80 whitespace-nowrap md:w-full ">
        <Table className="w-full  ">
          <TableHeader>
            <TableRow className="bg-gray-50 hover:bg-gray-50">
              <TableHead className="w-[50%] pl-1 text-xs md:text-base">Product</TableHead>
              <TableHead className="text-center text-xs md:text-base">Price</TableHead>
              <TableHead className="text-center text-xs md:text-base">Quantity</TableHead>
              <TableHead className="text-center -ml-10 text-xs md:text-base">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cartItems?.map((item) => {
              const currentQuantity = getItemQuantity(item);
              const isPending = pendingUpdates[item.id] !== undefined;
              const isMinusDisabled = currentQuantity <= 1;
              
              return (
                <TableRow key={item.id} className={isPending ? "bg-yellow-50" : ""}>
                  <TableCell className="font-medium py-2 px-1 md:py-4 md:px-6">
                    <div className="flex items-center  gap-4">
                      <div className="w-16 h-12 relative flex justify-center items-center rounded-md">
                        <img
                          src={item?.images?.[0] || item?.variant?.images?.[0] || item?.image || "/placeholder.webp"}
                          alt={item?.name || item?.variant?.name || item?.product?.name || "Product Image"}
                          className="object-fill rounded-md"
                        />
                      </div>
                      <div>
                        <h3 className="font-medium ">{item?.name || item?.variant?.name || item?.product?.name}</h3>
                        <p className="text-sm text-gray-500">
                          {item?.brand?.name || item?.variant?.brand?.name || item?.product?.brand || brands.find((brand) => brand.id === item.brand_id || item?.variant?.brand_id)?.name}
                        </p>
                        {isPending && (
                          <p className="text-xs text-yellow-600 font-medium">Pending update</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-center py-2 px-1 md:py-4 md:px-6">
                    ${(item?.price || item?.variant?.price || item?.product?.price || 0).toFixed(2)}
                  </TableCell>
                  <TableCell className="py-2 px-1 md:py-4 md:px-6">
                    <div className="flex items-center justify-center">
                      <Button
                        disabled={isUpdating || isRemoving || isMinusDisabled}
                        onClick={() => handleQuantityChange(item, currentQuantity - 1)}
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 rounded-md border"
                      >
                        <Minus className="h-3 w-3" />
                        <span className="sr-only">Decrease quantity</span>
                      </Button>
                      <div className="h-8 w-8 flex items-center justify-center ">
                        {currentQuantity}
                      </div>
                      <Button
                        disabled={isUpdating}
                        onClick={() => handleQuantityChange(item, currentQuantity + 1)}
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 rounded-md border"
                      >
                        <Plus className="h-3 w-3" />
                        <span className="sr-only">Increase quantity</span>
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="text-center py-2 px-1 md:py-4 md:px-6">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-20">${((item?.price || item?.variant?.price || item?.product?.price || 0) * currentQuantity)?.toFixed(2)}</div>
                      <Button
                        disabled={isUpdating || isRemoving}
                        onClick={() => handleRemoveFromCart(item)}
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-500"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Remove item</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ScrollArea>
    </>
  );
});

CartProductTable.displayName = "CartProductTable";
export default React.memo(CartProductTable);
